import { Component, NgZone } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { LoaderService } from '../../../services/loader.service'
import { AgentAuthService } from '../../../services/agent-auth.service'
import { AgentHeaderComponent } from '../../../components/agent-header/agent-header.component'
import { EMAIL_REGEXP } from '../../../common/validators/sharedRegex'

@Component({
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, AgentHeaderComponent],
    selector: 'app-agent-verification',
    templateUrl: './agent-verification.component.html',
    styleUrls: ['./agent-verification.component.scss']
})
export class AgentVerificationComponent {
  agentForm!: FormGroup
  submitted = false
  showPassword = false
  errorMessage = ''
  partnerIdError = ''
  passwordError = ''
  readonly passwordMinLength = 8
  readonly passwordMaxLength = 99

  constructor (
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private agentAuthService: AgentAuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit (): void {
    sessionStorage.removeItem('bookingType')

    // Initialise the form first so the template stays valid even while a
    // redirect (existing session) is in flight.
    this.agentForm = this.formBuilder.group({
      partnerId: ['', [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
      password: ['', [
        Validators.required,
        Validators.minLength(this.passwordMinLength),
        Validators.maxLength(this.passwordMaxLength)
      ]]
    })

    // If an agent session already exists (e.g. returning from the speaking page
    // within the same session), skip verification and go straight to tt-email.
    // Verification is only shown after logout or when the session is cleared.
    if (this.agentAuthService.hasAgentSession()) {
      this.ngZone.run(() => {
        this.router.navigate(['/agent/tt-email'], { queryParams: this.route.snapshot.queryParams })
      })
    }
  }

  get f () {
    return this.agentForm.controls
  }

  async onVerify (): Promise<void> {
    this.submitted = true
    this.errorMessage = ''
    if (this.agentForm.invalid) {
      return
    }
    this.loaderService.show()
    const { partnerId, password } = this.agentForm.value
    try {
      const result = await this.agentAuthService.agentSignIn(partnerId, password)
      if (result?.isSignedIn) {
        this.loaderService.hide()
        this.ngZone.run(() => {
          this.router.navigate(['/agent/tt-email'], { queryParams: this.route.snapshot.queryParams })
        })
      } else {
        this.loaderService.hide()
        this.errorMessage = 'Verification failed. Please try again.'
      }
    } catch (error: any) {
      this.loaderService.hide()
      if (error?.name === 'NotAuthorizedException') {
        this.errorMessage = 'Enter a valid password'
      } else if (error?.name === 'UserNotFoundException') {
        this.errorMessage = 'Partner account not found. Please try again.'
      } else if (error?.message?.includes('Password attempts exceeded')) {
        this.errorMessage = 'Attempt limit exceeded, please try after some time.'
      } else {
        this.errorMessage = 'Something went wrong. Please try again.'
      }
    }
  }

  onPartnerIdBlur (): void {
    const partnerId = this.agentForm.get('partnerId')
    if (partnerId?.errors?.['required']) {
      this.partnerIdError = 'Please enter the email linked to your partner account'
    } else if (partnerId?.errors?.['pattern']) {
      this.partnerIdError = 'Please enter a valid email address'
    } else {
      this.partnerIdError = ''
    }
  }

  onPasswordBlur (): void {
    const password = this.agentForm.get('password')
    if (password?.errors?.['minlength']) {
      this.passwordError = `Must be at least ${this.passwordMinLength} characters`
    } else if (password?.errors?.['maxlength']) {
      this.passwordError = `Must not exceed ${this.passwordMaxLength} characters`
    } else {
      this.passwordError = ''
    }
  }

  goBack (): void {
    window.history.back()
  }
}
